import React, { useState, useRef, useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet';

// ROUTER
import { Routes, Route } from 'react-router-dom';
import adminRoutes from 'routes/adminRoutes';

// CONTEXT
import { AuthContext } from 'context/AuthContext';
import { useMenu } from 'context/MenuContext';

// HOOKS
import useUserData from 'hooks/useUserData';

// SERVICES
import { getTileProposals } from 'services/tileService';

// COMPONENTS
import SidebarMenu from 'components/administration/sidebarMenuComponent';
import Header from 'components/administration/headerComponent';
import PageHeader from 'components/administration/pageHeaderComponent';
import Loader from 'components/loaderComponent';

function Admin() {
    const { email, authLoading } = useContext(AuthContext);
    const { setPendingProposals } = useMenu();
    const { userADInfo, userBDDInfo, loading: userDataLoading } = useUserData(email, true, false, true);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const searchInputRef = useRef(null);
    const headerRef = useRef(null);
    const pageHeaderRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [pageHeaderHeight, setPageHeaderHeight] = useState(0);
    const [pendingProposalsLoading, setPendingProposalsLoading] = useState(true);

    useEffect(() => {
        const handleShortcut = (e) => {
            if (e.ctrlKey && e.key.toLowerCase() === 'k') {
                e.preventDefault()
                searchInputRef.current?.focus()
            }
        }

        window.addEventListener('keydown', handleShortcut)
        return () => window.removeEventListener('keydown', handleShortcut)
    }, [])

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isSidebarOpen && window.innerWidth < 768) {
                setIsSidebarOpen(false)
            }
        }

        window.addEventListener('keydown', handleEscape)
        return () => window.removeEventListener('keydown', handleEscape)
    }, [isSidebarOpen])

    useEffect(() => {
        const updateHeights = () => {
            setHeaderHeight(headerRef.current?.offsetHeight || 0);
            setPageHeaderHeight(pageHeaderRef.current?.offsetHeight || 0);
        };

        updateHeights();
        window.addEventListener('resize', updateHeights);

        return () => {
            window.removeEventListener('resize', updateHeights);
        };
    }, []);

    useEffect(() => {
        // Récupérer le nombre de propositions en attente dès l'arrivée sur la page admin
        const fetchPendingProposals = async () => {
            setPendingProposalsLoading(true);
            try {
                const response = await getTileProposals();
                const data = response.data;
                const pendingCount = data.filter(proposal => !proposal.PRP_VALIDATED_AT && !proposal.PRP_DECLINED_AT).length;
                setPendingProposals(pendingCount);
            } catch (error) {
                setPendingProposals(0);
            } finally {
                setPendingProposalsLoading(false);
            }
        };
        fetchPendingProposals();
    }, [setPendingProposals]);

    useEffect(() => {
        const handleFullscreenToggle = (event) => {
            setIsFullscreen(event.detail);
            if (event.detail) {
                setIsSidebarOpen(false);
            }
        };

        window.addEventListener('toggleFullscreen', handleFullscreenToggle);
        return () => window.removeEventListener('toggleFullscreen', handleFullscreenToggle);
    }, []);

    if (authLoading || userDataLoading || pendingProposalsLoading) {
        return <Loader />
    }

    return (
        <>
        <Helmet>
            <title>Administration - HUB Altitude</title>
            <meta name="description" content="Espace d'administration du HUB Altitude Infra. Réservé aux utilisateurs autorisés." />
        </Helmet>
        <div className="flex w-full h-screen overflow-hidden text-noir dark:text-blanc">
            {/* Sidebar */}
            {!isFullscreen && (
                <SidebarMenu
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                />
            )}

            {isSidebarOpen && !isFullscreen && (
                <div
                    className="fixed inset-0 bg-noir/60 z-5 md:hidden"
                    onClick={() => setIsSidebarOpen(false)}
                />
            )}

            {/* Main Content Area */}
            <div
                className={`bg-blanc dark:bg-noir flex flex-col flex-1 
                transition-[margin] duration-500 ease-in-out 
                ${isSidebarOpen && !isFullscreen ? 'ml-0 md:ml-80 z-0 md:z-10' : 'ml-0 z-10'}
                w-full overflow-hidden`}
            >
                {/* Header */}
                {!isFullscreen && (
                    <Header
                        ref={headerRef}
                        isSidebarOpen={isSidebarOpen} 
                        setIsSidebarOpen={setIsSidebarOpen} 
                        searchRef={searchInputRef}
                        email={email}
                    />
                )}
    
                {/* Page Header */}
                {!isFullscreen && (
                    <PageHeader ref={pageHeaderRef} />
                )}

                {/* Content */}
                <main 
                    className="px-6 py-2 flex-1 overflow-y-auto min-h-0 w-full"
                    style={{ 
                        height: isFullscreen 
                            ? '100vh' 
                            : `calc(100vh - ${headerHeight + pageHeaderHeight}px)`
                    }}
                >
                    <Routes>
                        {adminRoutes.map(({ path, element }) => (
                            <Route key={path} path={path} element={element} />
                        ))}
                    </Routes>
                </main>
            </div>
        </div>
        </>
    );
}

export default Admin;